import type { ComplianceRequirementCard, UseCaseEntry } from "@regpulse/contracts";

/**
 * Board-aware dedup for U · Use Case Intelligence (ADR-0008).
 *
 * The seed catalog is a static landscape; ABCD cards come from the live evaluator.
 * Without dedup, partial rows re-list the same REG-* ids and planned rows repeat
 * scenarios that already have obligation cards (REG-AD-*, REG-DA-*, REG-RMI-*, …).
 */

/** v0.1 declared-scope core — same set as use-case-catalog honesty tests. */
export const DECLARED_CORE_RULE_IDS = new Set([
  "REG-TA-001",
  "REG-GSR-001",
  "REG-GSR-002",
  "REG-PV-001",
  "REG-CS-001",
  "REG-CS-002",
]);

/**
 * When ANY applicable board card matches these hints, hide the planned catalog row.
 * Prefixes match card.id; tokens match id + title + legal instrument text (lowercased).
 */
const PLANNED_BOARD_OVERLAP: Record<
  string,
  { ruleIdPrefixes?: string[]; ruleIds?: string[]; instrumentTokens?: string[] }
> = {
  "uc-dcas": {
    ruleIdPrefixes: ["REG-AD-", "REG-CS-001", "REG-AI-"],
    instrumentTokens: ["r171", "dcas", "r155"],
  },
  "uc-l3-alks": {
    ruleIdPrefixes: ["REG-AD-"],
    instrumentTokens: ["r157", "alks"],
  },
  "uc-parking-rpa": {
    ruleIdPrefixes: ["REG-AD-", "REG-UN-079"],
    instrumentTokens: ["r79", "remote parking", "rpa"],
  },
  "uc-remote-diagnostics": {
    ruleIdPrefixes: ["REG-DA-", "REG-PV-"],
    instrumentTokens: ["data act", "gdpr", "2854"],
  },
  "uc-rmi-aftersales": {
    ruleIdPrefixes: ["REG-RMI-", "REG-TA-"],
    instrumentTokens: ["rmi", "2018/858", "repair and maintenance"],
  },
  "uc-insurance-ubi": {
    ruleIdPrefixes: ["REG-PV-", "REG-DA-", "MF-INS-"],
    instrumentTokens: ["gdpr", "telematics", "insurer"],
  },
  "uc-fleet-leasing": {
    ruleIdPrefixes: ["REG-PV-", "REG-DA-", "MF-FLEET-"],
    instrumentTokens: ["gdpr", "data act", "fleet"],
  },
  "uc-digital-key": {
    ruleIdPrefixes: ["REG-CS-"],
    instrumentTokens: ["r155", "digital key", "cybersecurity"],
  },
};

function applicableBoardCards(
  cards: ComplianceRequirementCard[] | undefined,
): ComplianceRequirementCard[] {
  if (!cards?.length) return [];
  return cards.filter(
    (c) =>
      c.applicabilityCategory === "applicable" &&
      (c.layer === "A" || c.layer === "B" || c.layer === "C" || c.layer === "D" || c.layer === "P"),
  );
}

function cardSearchText(card: ComplianceRequirementCard): string {
  const refs = card.legalReferences.map((r) => r.instrument).join(" ");
  return `${card.id} ${card.title} ${card.requirementText} ${refs}`.toLowerCase();
}

function boardTouchesHints(
  board: ComplianceRequirementCard[],
  hints: (typeof PLANNED_BOARD_OVERLAP)[string],
): boolean {
  for (const card of board) {
    if (hints.ruleIds?.includes(card.id)) return true;
    if (hints.ruleIdPrefixes?.some((p) => card.id.startsWith(p))) return true;
    if (hints.instrumentTokens?.length) {
      const hay = cardSearchText(card);
      if (hints.instrumentTokens.some((t) => hay.includes(t.toLowerCase()))) return true;
    }
  }
  return false;
}

function shouldHidePartialEntry(
  entry: UseCaseEntry,
  boardRuleIds: ReadonlySet<string>,
): boolean {
  if (entry.canonicalRuleIds.length === 0) return false;
  return entry.canonicalRuleIds.every((id) => boardRuleIds.has(id));
}

function shouldHidePlannedEntry(
  entry: UseCaseEntry,
  board: ComplianceRequirementCard[],
): boolean {
  const hints = PLANNED_BOARD_OVERLAP[entry.id];
  if (!hints) return false;
  return boardTouchesHints(board, hints);
}

export type FilterUseCaseCatalogOptions = {
  /** Applicable A–D / P obligation cards from the current evaluation. Omit before Run. */
  boardCards?: ComplianceRequirementCard[];
};

/**
 * Returns catalog rows safe to show in U without re-listing the ABCD board.
 * Always drops `complete`. When board cards are present, also drops partial/planned
 * rows that are already represented as obligation cards.
 */
export function filterUseCaseCatalog(
  entries: UseCaseEntry[],
  options: FilterUseCaseCatalogOptions = {},
): UseCaseEntry[] {
  const board = applicableBoardCards(options.boardCards);
  const boardRuleIds = new Set(board.map((c) => c.id));

  return entries.filter((entry) => {
    if (entry.coverageStatus === "complete") return false;

    if (board.length === 0) {
      return true;
    }

    if (entry.coverageStatus === "partial") {
      return !shouldHidePartialEntry(entry, boardRuleIds);
    }

    if (entry.coverageStatus === "planned") {
      return !shouldHidePlannedEntry(entry, board);
    }

    return true;
  });
}
